import { Sparkles, AlertCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { isAiCellRunning } from "./ai-cell-state";

/**
 * One AI column's answer for one deal.
 *
 * Four states, and the distinction that matters most is empty vs running: a
 * cell that has never been asked looks like an invitation, and one that is
 * working looks busy. Conflating them makes an expensive operation feel like it
 * did nothing.
 */

export type AiCellValue = {
  value?: unknown;
  status?: "running" | "done" | "error";
  run_at?: string;
  error?: string;
  /** Why the model answered as it did — the only explanation of an empty cell. */
  reasoning?: string;
};

const formatValue =(value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (type === "boolean") return value ? "Yes" : "No";
  if (type === "currency" && typeof value === "number") {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  }
  if (type === "date") {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime())
      ? String(value)
      : date.toLocaleDateString();
  }
  return String(value);
};

export function AiColumnCell({
  cell,
  type,
  onRun,
  disabled,
}: {
  cell: AiCellValue | undefined;
  type: string;
  onRun: () => void;
  disabled?: boolean;
}) {
  if (isAiCellRunning(cell)) {
    const previous = formatValue(cell?.value, type);
    const hasPrevious = previous !== "—";

    // A re-run keeps the answer it is replacing on screen, dimmed, next to the
    // spinner. Blanking the column while every cell reruns loses the very
    // numbers the table is being read for, and for several seconds at that.
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
        title={hasPrevious ? "Updating this answer" : "Working on this answer"}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        {hasPrevious ? (
          <span
            className={cn(
              "text-sm opacity-50",
              type === "number" || type === "currency"
                ? "font-mono tabular-nums"
                : ""
            )}
          >
            {previous}
          </span>
        ) : (
          "Working…"
        )}
      </span>
    );
  }

  // A run that outlived the cutoff is as good as failed: offering the retry is
  // the only way out of a spinner nothing is ever going to stop.
  const timedOut = cell?.status === "running";

  if (cell?.status === "error" || timedOut) {
    return (
      <button
        type="button"
        onClick={onRun}
        disabled={disabled}
        title={
          timedOut
            ? "This run never finished. Click to try again."
            : cell?.error || "Something went wrong. Click to try again."
        }
        className="inline-flex items-center gap-1.5 text-xs text-destructive hover:underline"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Failed — retry
      </button>
    );
  }

  if (cell?.status === "done" || cell?.value !== undefined) {
    const isEmpty =
      cell?.value === null || cell?.value === undefined || cell?.value === "";

    return (
      <span className="group/cell inline-flex items-center gap-1.5">
        {/* An answered-but-empty cell is a real answer — "it isn't in the
            documents" — and saying so is the difference between a column that
            looks broken and one that looks honest. A bare dash reads as a
            button that did nothing. */}
        <span
          className={cn(
            "text-sm",
            isEmpty ? "text-muted-foreground" : "text-foreground",
            !isEmpty && (type === "number" || type === "currency")
              ? "font-mono tabular-nums"
              : ""
          )}
          title={isEmpty ? cell?.reasoning || undefined : undefined}
        >
          {isEmpty ? "Not found" : formatValue(cell?.value, type)}
        </span>
        {/* Re-run stays out of the way until the row is hovered — the value is
            what you came to read, not the button. On an empty cell there is no
            value to read, so the way to act on it should not be hidden. */}
        <button
          type="button"
          onClick={onRun}
          disabled={disabled}
          aria-label="Run again"
          title="Run again"
          className={cn(
            "transition-opacity focus-visible:opacity-100 disabled:opacity-40",
            isEmpty
              ? "opacity-60 hover:opacity-100"
              : "opacity-0 group-hover:opacity-100"
          )}
        >
          <Sparkles className="h-3 w-3 text-muted-foreground hover:text-primary" />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onRun}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-primary"
    >
      <Sparkles className="h-3.5 w-3.5" />
      Run
    </button>
  );
}
